import { Stack } from '@mantine/core';
import {
  IconBuildingSkyscraper,
  IconHome,
  IconMapPin,
  IconMessages,
  IconPresentationAnalytics,
} from '@tabler/icons-react';
import { Link, useRouterState } from '@tanstack/react-router';
import { bareList } from '~portal/components/common/common.css';
import { Cmp } from '~portal/lib';
import { isRestrictedPath } from '~portal/lib/edge-gate';
import * as styles from './GlobalNav.css';
import { RestrictedNavGroup } from './RestrictedNavGroup';

/**
 * The platform's sections, in Figma's nav order (`12:780`). One list feeds every presentation —
 * the desktop column, the mobile drawer and the mobile header's section label — so a section
 * cannot arrive in one and not another.
 *
 * `match` is the path prefix that marks a section current and `link` is where it goes; they
 * differ only for the Agent, whose section is every `/chat/*` path but whose entry opens a new
 * conversation. Icons are the closest Tabler glyphs to Figma's custom set (docs/figma-deviations.md).
 */
export const sections = [
  { label: 'Home', match: '/', exact: true, Icon: IconHome, link: { to: '/' } },
  {
    label: 'Agent',
    match: '/chat',
    exact: false,
    Icon: IconMessages,
    link: { to: '/chat/$conversationId', params: { conversationId: 'new' } },
  },
  {
    label: 'Operators',
    match: '/operators',
    exact: false,
    Icon: IconBuildingSkyscraper,
    link: { to: '/operators' },
  },
  { label: 'Regions', match: '/regions', exact: false, Icon: IconMapPin, link: { to: '/regions' } },
  {
    label: 'Reports',
    match: '/reports',
    exact: false,
    Icon: IconPresentationAnalytics,
    link: { to: '/reports' },
  },
] as const;

export type NavSection = (typeof sections)[number];

/**
 * @desc Whether `path` is inside `section`. Home is exact (every path starts with `/`); the rest
 * match their prefix on a segment boundary, so `/regionsfoo` would not light Regions.
 */
export const isSectionActive = (path: string, section: NavSection): boolean =>
  section.exact
    ? path === section.match
    : path === section.match || path.startsWith(`${section.match}/`);

/**
 * @desc What the mobile header calls the current screen: its section, or — for the places outside
 * the five sections — "Admin", "Account" and "Restricted".
 */
export const currentSectionLabel = (path: string): string => {
  if (path === '/admin' || path.startsWith('/admin/')) return 'Admin';
  if (isRestrictedPath(path)) return 'Restricted';
  if (path === '/account' || path.startsWith('/account/')) return 'Account';
  return sections.find((section) => isSectionActive(path, section))?.label ?? '';
};

export type GlobalNavItemsProps = {
  /** Called when an item is chosen — the drawer closes itself with it. */
  onNavigate?: () => void;
};

/**
 * @desc The five section pills, shared by the desktop column and the mobile drawer. The current
 * section carries `data-selected` (the pill's selected tint); the router adds `aria-current`.
 * The admin-only Restricted group follows them when it applies, so both presentations carry it.
 */
export const GlobalNavItems: Cmp<GlobalNavItemsProps> = function ({ onNavigate }) {
  const path = useRouterState({ select: (state) => state.location.pathname });

  return (
    <>
      <Stack component="ul" gap="md" className={bareList}>
        {sections.map((section) => {
          const active = isSectionActive(path, section);
          return (
            <li key={section.label}>
              <Link
                {...section.link}
                className={styles.item}
                // The router stamps `aria-current="page"` on whatever it considers active, and its
                // default is prefix matching — which would claim Home on every page. Exact for
                // Home only, so its idea of "current" agrees with `isSectionActive`.
                activeOptions={{ exact: section.exact }}
                data-selected={active || undefined}
                onClick={onNavigate}
              >
                <section.Icon size={22} stroke={1.5} aria-hidden />
                {section.label}
              </Link>
            </li>
          );
        })}
      </Stack>
      <RestrictedNavGroup onNavigate={onNavigate} />
    </>
  );
};
